import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material';
import { Group, Add, Edit, Delete } from '@mui/icons-material';
import ImportExportBar from '../../components/ImportExport/ImportExportBar';
import { emitDataUpdated } from '../../utils/dataBus';
import { logAuditEvent } from '../../utils/audit';

interface TiersItem {
  id: string;
  type: 'client' | 'fournisseur' | 'autre';
  nom: string;
  contact?: string;
  email?: string;
  telephone?: string;
  actif: boolean;
}

const STORAGE_KEY = 'comptaEBNL_tiers_local';

const Tiers: React.FC = () => {
  const [tiers, setTiers] = useState<TiersItem[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TiersItem | null>(null);
  const [form, setForm] = useState<TiersItem>({ id: '', type: 'client', nom: '', contact: '', email: '', telephone: '', actif: true });

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) { try { setTiers(JSON.parse(raw)); } catch { setTiers([]); } }
  }, []);

  const saveAll = (items: TiersItem[]) => {
    setTiers(items);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    emitDataUpdated();
    logAuditEvent({ module: 'tiers', action: 'save_all', details: { count: items.length } });
  };

  const handleNew = () => {
    setEditing(null);
    setForm({ id: '', type: 'client', nom: '', contact: '', email: '', telephone: '', actif: true });
    setOpen(true);
  };

  const handleEdit = (t: TiersItem) => {
    setEditing(t);
    setForm({ ...t });
    setOpen(true);
  };

  const handleDelete = (id: string) => {
    saveAll(tiers.filter(t => t.id !== id));
    logAuditEvent({ module: 'tiers', action: 'delete', details: { id } });
  };

  const upsert = () => {
    const id = editing?.id || `${Date.now()}`;
    const payload: TiersItem = { ...form, id };
    const next = [...tiers];
    const idx = next.findIndex(t => t.id === id);
    if (idx >= 0) next[idx] = payload; else next.unshift(payload);
    saveAll(next);
    setOpen(false);
    logAuditEvent({ module: 'tiers', action: editing ? 'update' : 'create', details: { id } });
  };

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length === 0) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const cols = line.split(',');
      const row: Record<string, string> = {};
      headers.forEach((h, i) => { row[h] = (cols[i] || '').trim(); });
      return row;
    });
  };

  const onImportCSV = (csvText: string) => {
    // type,nom,contact,email,telephone,actif
    const rows = parseCSV(csvText);
    const list: TiersItem[] = rows.map((r) => ({
      id: `${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      type: (r.type as TiersItem['type']) || 'client',
      nom: r.nom || '',
      contact: r.contact || '',
      email: r.email || '',
      telephone: r.telephone || '',
      actif: String(r.actif || 'true').toLowerCase() !== 'false',
    }));
    saveAll(list);
    logAuditEvent({ module: 'tiers', action: 'import_csv', details: { count: list.length } });
  };

  const onImportJSON = (jsonText: string) => {
    try {
      const data = JSON.parse(jsonText);
      if (Array.isArray(data)) saveAll(data as TiersItem[]);
      logAuditEvent({ module: 'tiers', action: 'import_json', details: { count: Array.isArray(data) ? data.length : 0 } });
    } catch {}
  };

  const getExportCSV = () => {
    const header = 'type,nom,contact,email,telephone,actif';
    const lines = [header, ...tiers.map(t => [t.type, t.nom.replace(/,/g,' '), t.contact?.replace(/,/g,' ') || '', t.email || '', t.telephone || '', t.actif ? 'true' : 'false'].join(','))];
    return { filename: 'tiers.csv', content: lines.join('\n') };
  };

  const getExportJSON = () => ({ filename: 'tiers.json', content: JSON.stringify(tiers, null, 2) });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestion des Tiers
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Clients, Fournisseurs et autres partenaires (mode local)
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group color="primary" />
              <Typography variant="h6">Liste des tiers</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <ImportExportBar onImportCSV={onImportCSV} onImportJSON={onImportJSON} getExportCSV={getExportCSV} getExportJSON={getExportJSON} />
              <Button variant="contained" startIcon={<Add />} onClick={handleNew}>
                Nouveau tiers
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Téléphone</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tiers.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">Aucun tiers</TableCell></TableRow>
            ) : tiers.map(t => (
              <TableRow key={t.id} hover>
                <TableCell>{t.type}</TableCell>
                <TableCell>{t.nom}</TableCell>
                <TableCell>{t.contact}</TableCell>
                <TableCell>{t.email}</TableCell>
                <TableCell>{t.telephone}</TableCell>
                <TableCell>
                  <Chip size="small" label={t.actif ? 'Actif' : 'Inactif'} color={t.actif ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(t)}><Edit fontSize="small"/></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(t.id)}><Delete fontSize="small"/></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {open && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {editing ? 'Modifier un tiers' : 'Nouveau tiers'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Type"
                  value={form.type}
                  onChange={e => setForm({ ...form, type: e.target.value as TiersItem['type'] })}
                >
                  {['client','fournisseur','autre'].map(t => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Nom"
                  value={form.nom}
                  onChange={e => setForm({ ...form, nom: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Contact"
                  value={form.contact}
                  onChange={e => setForm({ ...form, contact: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  value={form.telephone}
                  onChange={e => setForm({ ...form, telephone: e.target.value })}
                />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={() => setOpen(false)}>Annuler</Button>
              <Button variant="contained" onClick={upsert}>{editing ? 'Mettre à jour' : 'Enregistrer'}</Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Tiers;